import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Mail, MapPin, Phone, Send, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const departments = [
  "Sales",
  "Support",
  "Press",
  "Partnerships",
  "Careers",
  "General Inquiry"
];

const offices = [
  {
    city: "Dover, Delaware",
    address: "Duty AI Inc\n8 The Green, Ste A\nDover, DE 19901, USA",
    label: "Registered Office"
  }
];

const GetInTouchPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    department: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="bg-[#F9FBF5] min-h-screen">
        <section className="pt-20 lg:pt-32 pb-16 lg:pb-24">
          <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
            {/* Header */}
            <div className="mb-16 lg:mb-20">
              <h1 className="text-[#082900] text-[36px] lg:text-[64px] font-light leading-[1.05] tracking-[-0.02em] mb-4">
                Get in touch
              </h1>
              <p className="text-[#082900]/60 text-[16px] lg:text-[18px] font-normal max-w-xl">
                Have a question, want a demo, or just want to say hello? We'd love to hear from you.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
              {/* Form */}
              <div className="lg:col-span-3">
                {submitted ? (
                  <div className="bg-white rounded-2xl border border-[#082900]/5 p-10 lg:p-14 text-center">
                    <CheckCircle className="w-12 h-12 text-[#082900] mx-auto mb-5" strokeWidth={1.5} />
                    <h2 className="text-[#082900] text-[24px] lg:text-[32px] font-light mb-3">
                      Thank you!
                    </h2>
                    <p className="text-[#082900]/60 text-[15px] font-normal max-w-md mx-auto">
                      Your message has been received. Our team will get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Name */}
                      <div>
                        <label htmlFor="name" className="block text-[#082900] text-[13px] font-normal mb-2">
                          Full name <span className="text-[#082900]/30">*</span>
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          className="w-full px-4 py-3 bg-white border border-[#082900]/10 rounded-xl text-[#082900] text-[14px] placeholder:text-[#082900]/25 focus:outline-none focus:border-[#082900]/30 focus:ring-1 focus:ring-[#082900]/10 transition-colors"
                        />
                      </div>
                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-[#082900] text-[13px] font-normal mb-2">
                          Work email <span className="text-[#082900]/30">*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          placeholder="you@company.com"
                          className="w-full px-4 py-3 bg-white border border-[#082900]/10 rounded-xl text-[#082900] text-[14px] placeholder:text-[#082900]/25 focus:outline-none focus:border-[#082900]/30 focus:ring-1 focus:ring-[#082900]/10 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Company */}
                      <div>
                        <label htmlFor="company" className="block text-[#082900] text-[13px] font-normal mb-2">
                          Company
                        </label>
                        <input
                          id="company"
                          name="company"
                          type="text"
                          value={form.company}
                          onChange={handleChange}
                          placeholder="Your company"
                          className="w-full px-4 py-3 bg-white border border-[#082900]/10 rounded-xl text-[#082900] text-[14px] placeholder:text-[#082900]/25 focus:outline-none focus:border-[#082900]/30 focus:ring-1 focus:ring-[#082900]/10 transition-colors"
                        />
                      </div>
                      {/* Department */}
                      <div>
                        <label htmlFor="department" className="block text-[#082900] text-[13px] font-normal mb-2">
                          Department
                        </label>
                        <select
                          id="department"
                          name="department"
                          value={form.department}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-[#082900]/10 rounded-xl text-[#082900] text-[14px] focus:outline-none focus:border-[#082900]/30 focus:ring-1 focus:ring-[#082900]/10 transition-colors appearance-none"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23082900' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                        >
                          <option value="">Select a department</option>
                          {departments.map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-[#082900] text-[13px] font-normal mb-2">
                        Message <span className="text-[#082900]/30">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="How can we help?"
                        className="w-full px-4 py-3 bg-white border border-[#082900]/10 rounded-xl text-[#082900] text-[14px] placeholder:text-[#082900]/25 focus:outline-none focus:border-[#082900]/30 focus:ring-1 focus:ring-[#082900]/10 transition-colors resize-none"
                      />
                    </div>

                    {error && (
                      <p className="text-red-600 text-[13px]">{error}</p>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#082900] text-[#EEF2E1] text-[14px] font-normal rounded-full hover:bg-[#0a3500] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending...' : 'Send message'}
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Info Sidebar */}
              <div className="lg:col-span-2 space-y-10">
                {/* Email */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <Mail className="w-5 h-5 text-[#082900]/40" strokeWidth={1.5} />
                    <h3 className="text-[#082900] text-[14px] font-normal">Email us</h3>
                  </div>
                  <a href="mailto:hello@dutyai.in" className="text-[#082900]/70 text-[14px] hover:text-[#082900] transition-colors">
                    hello@dutyai.in
                  </a>
                </div>

                {/* Phone */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="w-5 h-5 text-[#082900]/40" strokeWidth={1.5} />
                    <h3 className="text-[#082900] text-[14px] font-normal">Call us</h3>
                  </div>
                  <a href="tel:+14155551234" className="text-[#082900]/70 text-[14px] hover:text-[#082900] transition-colors">
                    +1 (415) 555-1234
                  </a>
                </div>

                {/* Offices */}
                {offices.map((office, i) => (
                  <div key={i}>
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-[#082900]/40" strokeWidth={1.5} />
                      <h3 className="text-[#082900] text-[14px] font-normal">{office.label}</h3>
                    </div>
                    <p className="text-[#082900]/70 text-[14px] whitespace-pre-line leading-relaxed">
                      {office.address}
                    </p>
                  </div>
                ))}

                {/* Quick links */}
                <div className="pt-6 border-t border-[#082900]/10">
                  <h3 className="text-[#082900]/40 text-[13px] font-normal mb-4">Quick links</h3>
                  <div className="space-y-3">
                    <a href="/demo" className="block text-[#082900] text-[14px] font-normal hover:underline">
                      Request a demo →
                    </a>
                    <a href="/candidate-faq" className="block text-[#082900] text-[14px] font-normal hover:underline">
                      Candidate FAQ →
                    </a>
                    <a href="/careers" className="block text-[#082900] text-[14px] font-normal hover:underline">
                      We're hiring →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default GetInTouchPage;
